import { Badge, Banner, Button } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { SidePanel } from './ui/SidePanel';
import { PasswordField } from './ui/PasswordField';
import { ICONS, Glyph } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { ProviderLogo } from './ProviderLogo';

interface StripeView {
  configured: boolean;
  fromEnvironment: boolean;
  mode: 'live' | 'test' | null;
  keyHint: string | null;
  webhookConfigured: boolean;
  webhookEndpointId: string | null;
  webhookUrl: string;
}

interface StripeTest {
  ok: boolean;
  error?: string;
  account?: string;
  name?: string | null;
  country?: string;
  currency?: string;
  chargesEnabled?: boolean;
  pix?: string | null;
}

/** Stripe da plataforma (Plataforma › Integrações): cobrança das organizações. */
export function StripeCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<StripeView | null>(null);
  const [open, setOpen] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<StripeTest | null>(null);
  const [settingWebhook, setSettingWebhook] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/settings/stripe');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await fetchApi('/api/settings/stripe', { method: 'PUT', body: JSON.stringify({ secretKey }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Erro ao salvar a chave');
      setData(body);
      setOpen(false);
      setSecretKey('');
      toast.success('Chave do Stripe salva. Agora configure o webhook.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar a chave');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await fetchApi('/api/settings/stripe/test', { method: 'POST', body: '{}' });
      setTest(await res.json());
    } finally {
      setTesting(false);
    }
  };

  const setupWebhook = async () => {
    setSettingWebhook(true);
    try {
      const res = await fetchApi('/api/settings/stripe/webhook', { method: 'POST', body: '{}' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Erro ao configurar o webhook');
      setData(body);
      toast.success('Webhook do Stripe configurado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao configurar o webhook');
    } finally {
      setSettingWebhook(false);
    }
  };

  const configured = !!data?.configured;

  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center' }}><ProviderLogo id="stripe" size={44} /></span>
            {!configured ? (
              <Badge variant="neutral">Não configurado</Badge>
            ) : data?.webhookConfigured ? (
              <Badge variant={data.mode === 'live' ? 'green' : 'orange'}>{data.mode === 'live' ? 'Produção' : 'Teste'}</Badge>
            ) : (
              <Badge variant="orange">Falta o webhook</Badge>
            )}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>Stripe</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Assinaturas das organizações (cartão e Pix)</div>
          <div className="integration-host" title={data?.keyHint || undefined}>
            <ICONS.Key size={16} />
            <span>{data?.keyHint || 'sem chave'}{data?.fromEnvironment ? ' (ambiente)' : ''}</span>
          </div>
          {test && (
            <div className={`integration-test ${test.ok ? 'integration-test--ok' : 'integration-test--fail'}`} role="status">
              <Glyph icon={test.ok ? ICONS.CheckmarkWithCircle : ICONS.Warning} size={16}/>
              <span>
                {test.ok
                  ? `${test.name || test.account} · ${test.country} · ${test.chargesEnabled ? 'cobranças ativas' : 'cobranças desativadas'} · Pix ${test.pix === 'active' ? 'ativo' : 'inativo'}`
                  : test.error}
              </span>
            </div>
          )}
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" icon={configured ? ICONS.Edit : ICONS.Plus} onClick={() => { setError(null); setSecretKey(''); setOpen(true); }}>
            {configured ? 'Chave' : 'Configurar'}
          </Button>
          {configured && !data?.webhookConfigured && (
            <Button variant="primary" icon={ICONS.Link} loading={settingWebhook} onClick={setupWebhook}>
              Webhook
            </Button>
          )}
          <Button variant="secondary" icon={ICONS.Refresh} disabled={!configured} loading={testing} onClick={runTest}>
            Testar
          </Button>
        </div>
      </div>

      <SidePanel open={open} onClose={() => setOpen(false)} title="Conectar o Stripe" description="Cole a chave secreta da conta (Stripe › Desenvolvedores › Chaves de API). Ela fica só no servidor. Depois, o botão Configurar webhook registra o webhook no Stripe." formId="stripe-form" submitting={saving} submitLabel="Validar e salvar" submitDisabled={!secretKey.trim()}>
        <form id="stripe-form" noValidate onSubmit={save} className="flex flex-col gap-4">
          {error && <div style={{ marginBottom: 16 }}><Banner variant="error" description={<>{error}</>} /></div>}
          {data?.configured && (
            <div style={{ marginBottom: 16 }}>
              <Banner variant="alert" description={<>
                Trocar a chave desliga o webhook atual; configure-o de novo depois de salvar.
              </>} />
            </div>
          )}
          <PasswordField
            label="Chave secreta"
            placeholder="sk_test_… ou sk_live_…"
            autoComplete="off"
            value={secretKey}
            onChange={setSecretKey}
          />
        </form>
      </SidePanel>
    </>
  );
}
